/**
 * This code was GENERATED using the solita package.
 * Please DO NOT EDIT THIS FILE, instead rerun solita to update it or write a wrapper to add functionality.
 *
 * See: https://github.com/metaplex-foundation/solita
 */

import * as splToken from "@solana/spl-token";
import * as beet from "@metaplex-foundation/beet";
import * as web3 from "@solana/web3.js";
import { BidArgs, bidArgsBeet } from "../types/BidArgs";

/**
 * @category Instructions
 * @category Bid
 * @category generated
 */
export type BidInstructionArgs = {
  bidArgs: BidArgs;
};
/**
 * @category Instructions
 * @category Bid
 * @category generated
 */
export const BidStruct = new beet.BeetArgsStruct<
  BidInstructionArgs & {
    instructionDiscriminator: number;
  }
>(
  [
    ["instructionDiscriminator", beet.u8],
    ["bidArgs", bidArgsBeet],
  ],
  "BidInstructionArgs"
);
/**
 * Accounts required by the _Bid_ instruction
 *
 * @property [_writable_] saleAccount
 * @property [] transferAuthority
 * @property [] soldOutBidderIncentives
 * @property [] specificBidsIncentives
 * @property [_writable_] bidder
 * @property [**signer**] bidderAuthority
 * @property [_writable_] bid
 * @property [_writable_] bidAssetDest
 * @property [_writable_] bidAssetSrc
 * @property [] bidAssetMint
 * @property [_writable_] incentiveDest (optional)
 * @property [_writable_] incentiveSrc (optional)
 * @property [] incentiveMint (optional)
 * @category Instructions
 * @category Bid
 * @category generated
 */
export type BidInstructionAccounts = {
  saleAccount: web3.PublicKey;
  transferAuthority: web3.PublicKey;
  soldOutBidderIncentives: web3.PublicKey;
  specificBidsIncentives: web3.PublicKey;
  systemProgram?: web3.PublicKey;
  tokenProgram?: web3.PublicKey;
  ataProgram?: web3.PublicKey;
  bidder: web3.PublicKey;
  bidderAuthority: web3.PublicKey;
  bid: web3.PublicKey;
  bidAssetDest: web3.PublicKey;
  bidAssetSrc: web3.PublicKey;
  bidAssetMint: web3.PublicKey;
  incentiveDest?: web3.PublicKey;
  incentiveSrc?: web3.PublicKey;
  incentiveMint?: web3.PublicKey;
};

export const bidInstructionDiscriminator = 4;

/**
 * Creates a _Bid_ instruction.
 *
 * Optional accounts that are not provided will be omitted from the accounts
 * array passed with the instruction.
 * An optional account that is set cannot follow an optional account that is unset.
 * Otherwise an Error is raised.
 *
 * @param accounts that will be accessed while the instruction is processed
 * @param args to provide as instruction data to the program
 *
 * @category Instructions
 * @category Bid
 * @category generated
 */
export function createBidInstruction(
  accounts: BidInstructionAccounts,
  args: BidInstructionArgs,
  programId = new web3.PublicKey(process.env.REACT_APP_SOLANA_OUTBID_PROGRAM_ID)
) {
  const [data] = BidStruct.serialize({
    instructionDiscriminator: bidInstructionDiscriminator,
    ...args,
  });
  const keys: web3.AccountMeta[] = [
    {
      pubkey: accounts.saleAccount,
      isWritable: true,
      isSigner: false,
    },
    {
      pubkey: accounts.transferAuthority,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: accounts.soldOutBidderIncentives,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: accounts.specificBidsIncentives,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: accounts.systemProgram ?? web3.SystemProgram.programId,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: accounts.tokenProgram ?? splToken.TOKEN_PROGRAM_ID,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: accounts.ataProgram ?? splToken.ASSOCIATED_TOKEN_PROGRAM_ID,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: accounts.bidder,
      isWritable: true,
      isSigner: false,
    },
    {
      pubkey: accounts.bidderAuthority,
      isWritable: false,
      isSigner: true,
    },
    {
      pubkey: accounts.bid,
      isWritable: true,
      isSigner: false,
    },
    {
      pubkey: accounts.bidAssetDest,
      isWritable: true,
      isSigner: false,
    },
    {
      pubkey: accounts.bidAssetSrc,
      isWritable: true,
      isSigner: false,
    },
    {
      pubkey: accounts.bidAssetMint,
      isWritable: false,
      isSigner: false,
    },
  ];

  if (accounts.incentiveDest != null) {
    keys.push({
      pubkey: accounts.incentiveDest,
      isWritable: true,
      isSigner: false,
    });
  }
  if (accounts.incentiveSrc != null) {
    if (accounts.incentiveDest == null) {
      throw new Error("When providing 'incentiveSrc' then 'accounts.incentiveDest' need(s) to be provided as well.");
    }
    keys.push({
      pubkey: accounts.incentiveSrc,
      isWritable: true,
      isSigner: false,
    });
  }
  if (accounts.incentiveMint != null) {
    if (accounts.incentiveDest == null || accounts.incentiveSrc == null) {
      throw new Error(
        "When providing 'incentiveMint' then 'accounts.incentiveDest', 'accounts.incentiveSrc' need(s) to be provided as well."
      );
    }
    keys.push({
      pubkey: accounts.incentiveMint,
      isWritable: false,
      isSigner: false,
    });
  }

  const ix = new web3.TransactionInstruction({
    programId,
    keys,
    data,
  });
  return ix;
}
